import * as React from 'react'
import type { CSSProperties } from 'react'
import { cn } from '../lib'
import { designTokens } from '../lib/design-tokens'
import { typographyStyle } from '../lib/catalog'
import { SearchIcon } from './icons'
import { Input } from './input'

export type InputFieldProps = React.InputHTMLAttributes<HTMLInputElement> & {
  variant?: 'default' | 'search'
  label?: string
  labelClassName?: string
  labelStyle?: CSSProperties
  helperText?: string
  prefix?: React.ReactNode
  suffix?: React.ReactNode
  containerClassName?: string
  containerStyle?: CSSProperties
  inputClassName?: string
  helperClassName?: string
  prefixClassName?: string
  prefixStyle?: CSSProperties
}

const InputField = React.forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      variant = 'default',
      label,
      helperText,
      prefix,
      suffix,
      containerClassName,
      containerStyle,
      inputClassName,
      helperClassName,
      id,
      className,
      labelClassName,
      labelStyle,
      prefixClassName,
      prefixStyle,
      style: inputStyleOverride,
      ...props
    },
    ref
  ) => {
    const inputId = id ?? props.name
    const isSearch = variant === 'search'
    const resolvedPrefix = isSearch && !prefix ? <SearchIcon /> : prefix
    const resolvedPrefixStyle = isSearch
      ? {
          left: designTokens.components.account.search.iconOffsetX,
          ...prefixStyle,
        }
      : prefixStyle
    const resolvedInputStyle = {
      ...(isSearch
        ? {
            paddingLeft: `calc(${designTokens.components.account.search.iconOffsetX} + ${designTokens.components.account.search.iconSize} + ${designTokens.components.account.search.textGap})`,
          }
        : undefined),
      ...inputStyleOverride,
    }
    const labelTokenStyle: CSSProperties = {
      fontFamily: designTokens.typography.fontFamilies.primary,
      color: designTokens.colors.text.primary,
      ...typographyStyle(designTokens.typography.label),
    }

    const helperTokenStyle: CSSProperties = {
      fontFamily: designTokens.typography.fontFamilies.primary,
      color: designTokens.colors.text.secondary,
      ...typographyStyle(designTokens.typography.caption),
    }

    return (
      <div className={cn('flex flex-col gap-2', containerClassName)} style={containerStyle}>
        {label ? (
          <label
            htmlFor={inputId}
            className={cn(labelClassName)}
            style={{ ...labelTokenStyle, ...labelStyle }}
          >
            {label}
          </label>
        ) : null}
        <div className="relative">
          {resolvedPrefix ? (
            <span
              className={cn(
                'pointer-events-none absolute inset-y-0 left-3 flex items-center',
                prefixClassName
              )}
              style={{ color: designTokens.colors.text.muted, ...resolvedPrefixStyle }}
            >
              {resolvedPrefix}
            </span>
          ) : null}
          <Input
            id={inputId}
            ref={ref}
            className={cn(
              resolvedPrefix ? 'pl-10' : undefined,
              suffix ? 'pr-10' : undefined,
              inputClassName,
              className
            )}
            style={resolvedInputStyle}
            {...props}
          />
          {suffix ? (
            <span
              className="absolute inset-y-0 right-3 flex items-center"
              style={{ color: designTokens.colors.text.muted }}
            >
              {suffix}
            </span>
          ) : null}
        </div>
        {helperText ? (
          <p className={cn(helperClassName)} style={helperTokenStyle}>
            {helperText}
          </p>
        ) : null}
      </div>
    )
  }
)
InputField.displayName = 'InputField'

export type PasswordInputProps = Omit<InputFieldProps, 'type' | 'suffix'> & {
  defaultVisible?: boolean
}

const EyeIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path
      d="M12 5c5.05 0 9.27 3.11 11 7-1.73 3.89-5.95 7-11 7S2.73 15.89 1 12c1.73-3.89 5.95-7 11-7zm0 2c-3.73 0-6.93 2.1-8.59 5 1.66 2.9 4.86 5 8.59 5s6.93-2.1 8.59-5C18.93 9.1 15.73 7 12 7zm0 2.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5z"
      fill="currentColor"
    />
  </svg>
)

const EyeOffIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path
      d="M3.28 2L22 20.72l-1.41 1.41-3.06-3.06A11.4 11.4 0 0 1 12 19C6.95 19 2.73 15.89 1 12c.82-1.84 2.21-3.48 3.97-4.75L1.87 4.41 3.28 3l-.01-.01zm7.15 5.73L8.77 6.07A9.03 9.03 0 0 1 12 5c5.05 0 9.27 3.11 11 7-.63 1.43-1.57 2.73-2.77 3.79l-2.49-2.49a4 4 0 0 0-5.31-5.31zm1.56 1.56a1.5 1.5 0 0 0-1.7 1.7l1.7-1.7zm-1.98 5.53a4 4 0 0 0 5.55-5.55l-1.43-1.43a2.5 2.5 0 0 1-3.69 3.69l-1.43-1.43a2.5 2.5 0 0 0 3.69 3.69l-2.69-2.69z"
      fill="currentColor"
    />
  </svg>
)

const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ defaultVisible = false, ...props }, ref) => {
    const [visible, setVisible] = React.useState(defaultVisible)
    return (
      <InputField
        ref={ref}
        type={visible ? 'text' : 'password'}
        suffix={
          <button
            type="button"
            aria-label={visible ? 'Hide password' : 'Show password'}
            className="flex h-6 w-6 items-center justify-center"
            onClick={() => setVisible((value) => !value)}
          >
            {visible ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
          </button>
        }
        {...props}
      />
    )
  }
)
PasswordInput.displayName = 'PasswordInput'

export type PromoCodeInputProps = InputFieldProps

const PromoCodeInput = React.forwardRef<HTMLInputElement, PromoCodeInputProps>((props, ref) => (
  <InputField ref={ref} {...props} />
))
PromoCodeInput.displayName = 'PromoCodeInput'

export { InputField, PasswordInput, PromoCodeInput }
